import { css } from '@emotion/react'
import type { AchievementLevel } from '@mathflat/domain/Achievement'
import type { ConceptChip } from '@mathflat/domain/ConceptChip'
import { s3URL } from '@mathflat/shared/@common/utils/s3'
import { observer } from 'mobx-react'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { useRepository } from '~/@common/hooks/useRepository'
import modalService from '~/@common/services/modal.service'
import { commonRepo } from '~/@common/services/repo.service'
import { textEllipsis, typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { Icon } from '~/@common/ui/Icon/Icon'
import Modal from '~/@common/ui/modal/Modal'
import { mixpanelClient } from '~/@common/utils/trackings/mixpanel'
import ConceptChipAchievementLevel from '~/@pages/@common/(ConceptChip)/ConceptChipAchievmentLevel/ConceptChipAchievementLevel'
import { LearningUnavailableAlertModal } from '~/@pages/@widgets/LearningUnavailableAlertModal'
import { ChallengeStore } from '~/@pages/student/@common/store/Challenge.store'
import { LearningProcessService } from '~/@pages/student/learning-process/@widgets/service/LearningProcess.service'

import RecentChallengeByCurriculumService from '../../@service/RecentChallengeByCurriculum.service'

const LEVEL_TYPES = ['개념', '기본', '심화'] as const

const RecentChallengeByCurriculumSection = observer(() => {
  const service = useRepository(RecentChallengeByCurriculumService)
  const learningProcessService = useRepository(LearningProcessService)
  const challengeStore = useRepository(ChallengeStore)
  const navigate = useNavigate()
  const studentId = commonRepo.studentId

  useEffect(() => {
    if (!studentId) return
    service.fetchRecentChallengeByCurriculum(studentId)
  }, [service, studentId])

  const achievementMap = useMemo(
    () => new Map(service.achievements.map((a) => [a.conceptChipId, a.achievementLevel])),
    [service.achievements],
  )

  const getAchievementLevel = (id: number) => achievementMap.get(id) ?? 'WHITE'

  const 학습불가_안내 = () => {
    modalService.openModal(<LearningUnavailableAlertModal />, {
      modalName: '학습 불가',
    })
  }

  const startLearning = async (conceptChipId: number) => {
    const littleChapterId = service.currentLittleChapterConceptChips?.littleChapterId
    if (!littleChapterId) return

    try {
      const 챌린지학습 = await learningProcessService.챌린지학습_시작하기({
        conceptChipIds: [conceptChipId],
        littleChapterId,
        referenceId: conceptChipId,
        referenceType: 'CONCEPT_CHIP',
      })

      if (챌린지학습.시작 === '불가') {
        학습불가_안내()
        return
      }

      if (챌린지학습.시작 === '이어하기') {
        modalService.openModal(
          <Modal.Confirm.Positive
            css={{ width: 266 }}
            cancel={{
              children: '새로하기',
              onClick: async () => {
                const 이동 = await 챌린지학습.새로하기()
                if (이동.결과 === '불가') {
                  학습불가_안내()
                  return
                }
                navigate(이동.url)
              },
            }}
            confirm={{
              children: '이어하기',
              onClick: async () => {
                const 이동 = await 챌린지학습.이어하기()
                if (이동.결과 === '불가') {
                  학습불가_안내()
                  return
                }
                challengeStore.clearSelectedChallenge()
                modalService.closeModal()
                navigate(이동.url)
              },
            }}
          >
            <p>
              학습중이던 유형 챌린지가 있습니다.
              <br />
              이어서 하시겠습니까?
            </p>
          </Modal.Confirm.Positive>,
          { modalName: '유형 챌린지 이어하기' },
        )
        return
      }

      const 이동 = await 챌린지학습.새로하기()
      if (이동.결과 === '불가') {
        학습불가_안내()
        return
      }
      challengeStore.clearSelectedChallenge()
      navigate(이동.url)
    } catch {
      학습불가_안내()
    }
  }

  const handleClickConceptChip = (conceptChipId: number) => {
    startLearning(conceptChipId)
  }

  return (
    <div css={S.Container}>
      <header css={S.Header}>
        <div className="info-wrapper">
          모두 스마일로 바꿔요
          <Icon
            name="icon_info_fill"
            size={24}
            color="var(--Colors-Gray-900)"
            onClick={() => {
              modalService.openModal(
                <div css={S.TooltipModal}>
                  <img
                    src={s3URL.student('images/smile_challenge_guide.svg')}
                    alt="recent-challenge-by-curriculum-tooltip"
                  />
                </div>,
                {
                  modalName: '모두 스마일로 바꿔요',
                  hasCloseButton: true,
                },
              )
            }}
          />
        </div>
        <div className="button-wrapper">
          <button
            onClick={() => {
              if (!studentId) return
              service.navigateToPrevious(studentId)
              mixpanelClient.track('스마일바꾸기영역_이전_Click')
            }}
            disabled={!service.canNavigatePrevious}
          >
            <Icon name="icon_chevron_left" size={20} color="var(--Colors-Gray-800)" />
            <span className="button-text-desktop">이전</span>
          </button>

          <button
            onClick={() => {
              if (!studentId) return
              service.navigateToNext(studentId)
              mixpanelClient.track('스마일바꾸기영역_다음_Click')
            }}
            disabled={!service.canNavigateNext}
          >
            <span className="button-text-desktop">다음</span>
            <Icon name="icon_chevron_right" size={20} color="var(--Colors-Gray-800)" />
          </button>
        </div>
      </header>

      <div css={S.Content}>
        <div css={S.LittleChapterInfo}>
          <div className="title">
            {service.curriculumToStr}
            &nbsp;|&nbsp;{service.currentLittleChapterConceptChips?.littleChapterName}
          </div>
          <div className="description">
            {service.isAllSmile ? (
              <>
                <div className="mobile-description">대단해요, </div>모두 스마일이예요!
              </>
            ) : (
              <>
                <div className="mobile-description">스마일이 아닌 칸을 </div>선택해서 풀어요
              </>
            )}
          </div>
        </div>

        <div css={S.ConceptChipWrapper}>
          {LEVEL_TYPES.map((type) => (
            <ConceptLevelSection
              key={type}
              title={type}
              items={service[`${type}_유형칩리스트`] ?? []}
              getLevel={getAchievementLevel}
              onClick={handleClickConceptChip}
              curriculumInfo={service.curriculumToStr ?? ''}
            />
          ))}
        </div>
      </div>
    </div>
  )
})

export default RecentChallengeByCurriculumSection

type ConceptLevelSectionProps = {
  title: '개념' | '기본' | '심화'
  items: ConceptChip[]
  getLevel: (id: number) => AchievementLevel
  onClick: (id: number) => void
  curriculumInfo: string
}

const ConceptLevelSection = ({
  title,
  items,
  getLevel,
  onClick,
  curriculumInfo,
}: ConceptLevelSectionProps) => {
  return (
    <div className="concept-chip-level">
      <div className="concept-chip-title-wrapper">
        <div className="title">{title}</div>
        <div className="count">{items.length}개</div>
      </div>

      <div className="concept-chip-list">
        {items.map((item) => (
          <ConceptChipAchievementLevel
            key={item.conceptChipId}
            level={getLevel(item.conceptChipId)}
            checked={false}
            size={40}
            type="radio"
            onClick={() => {
              onClick(item.conceptChipId)

              mixpanelClient.track('스마일바꾸기영역_유형칩_Click', {
                유형분석칩: {
                  수준: title,
                  대단원명: item.bigChapterName,
                  중단원명: item.middleChapterName,
                  소단원명: item.littleChapterName,
                  유형명: item.conceptName,
                  컬러: getLevel(item.conceptChipId),
                },
                학년정보: curriculumInfo,
              })
            }}
          />
        ))}
      </div>
    </div>
  )
}

const S = {
  Container: css`
    display: flex;
    flex-direction: column;
    padding: 24px;
    background-color: var(--White);
    height: 100%;
    border-radius: var(--Radius-300);
    gap: 10px;
  `,

  Header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 700;
    color: var(--Colors-Gray-900);

    .info-wrapper {
      ${typo.heading03};
      display: flex;
      align-items: center;
      gap: 4px;

      svg {
        cursor: pointer;
      }
    }

    .button-wrapper {
      display: flex;
      gap: 10px;

      button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        border-radius: var(--Radius-100);
        border: 1px solid var(--Colors-Gray-300);
        padding: 12px;
        width: 80px;
        height: 40px;
        ${typo.body02};

        &:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        &:not(:disabled):hover {
          background-color: var(--Colors-Gray-100);
        }
      }
    }

    ${mediaQuery.underDesktop} {
      .info-wrapper {
        ${typo.heading04};
      }

      .button-text-desktop {
        display: none;
      }

      .button-wrapper {
        button {
          width: 40px;
        }
      }
    }
  `,

  Content: css`
    display: flex;
    flex-direction: column;
    border-radius: var(--Radius-300);
    height: 100%;
    border: 1px solid var(--Colors-Gray-300);
    overflow: hidden;
  `,

  LittleChapterInfo: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--Colors-Gray-300);
    padding: 14px 16px;
    background: var(--Colors-Gray-200);
    ${typo.body02};

    .title {
      ${textEllipsis(1)}
      font-weight: 700;
      color: var(--Colors-Gray-800);
    }

    .description {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      color: var(--Colors-Gray-600);
      min-width: 196px;
    }

    ${mediaQuery.underDesktop} {
      .mobile-description {
        display: none;
      }

      .description {
        min-width: 90px;
      }
    }
  `,

  ConceptChipWrapper: css`
    display: flex;
    gap: 10px;
    height: 100%;

    .concept-chip-level {
      flex: 1;
      display: flex;
      flex-direction: column;
      border-right: 1px solid var(--Colors-Gray-300);

      &:last-child {
        border-right: none;
      }
    }

    .concept-chip-title-wrapper {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 16px 12px;
      color: var(--Colors-Gray-800);

      .title {
        ${typo.body02};
        font-weight: 700;
      }

      .count {
        ${typo.caption01};
      }
    }

    .concept-chip-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      padding: 0 12px 16px;
      overflow-y: auto;
    }
  `,

  TooltipModal: css`
    width: 898px;
    img {
      width: 100%;
    }
  `,
}
